import * as THREE from 'three/webgpu'
import MouseFollowCharacterController from '../movements/MouseFollowCharacterController'
import Ball from './Ball'
import { randomGenome, mutateGenome, crossGenome, repairGenome } from './Genome'

const STORAGE_KEY = 'cwk.genomes.v1'
const MAX_CATS = 10
const FOUNDER_COUNT = 3
const REPRO_INTERVAL = 1.0           // seconds between reproduction rolls
const REPRO_MIN_SOCIABILITY = 0.55
const REPRO_MAX_DIST = 9
const REPRO_BASE_P = 0.08            // per tick, scaled by sociability
const EVO_INTERVAL = 60              // seconds between generational steps
const RETIRE_DURATION = 0.7          // seconds to fade out a retired cat

export default class World {
  constructor({ scene, camera, modelPath = '/models/fatcat/' }) {
    this.scene = scene
    this.camera = camera
    this.modelPath = modelPath
    this.cats = []
    this.generation = 1
    this._reproTimer = REPRO_INTERVAL
    this._evoTimer = EVO_INTERVAL
    this._saved = false
    this._retiring = new Set()

    this.ball = new Ball({ scene })

    this._bootstrap()

    // Persist genomes on unload (also pagehide for mobile Safari)
    const save = () => this.save()
    window.addEventListener('beforeunload', save)
    window.addEventListener('pagehide', save)
  }

  _bootstrap() {
    const saved = this._loadSaved()
    if (saved && Array.isArray(saved.genomes) && saved.genomes.length > 0) {
      this.generation = (saved.generation ?? 0) + 1
      // Respawn descendants of survivors — mild mutation
      const seed = saved.genomes.slice(0, FOUNDER_COUNT + 1).map(repairGenome)
      for (const g of seed) this.spawn(mutateGenome(g, 0.06))
    } else {
      this.generation = 1
      for (let i = 0; i < FOUNDER_COUNT; i++) this.spawn(randomGenome())
    }
  }

  spawn(genome, position = null) {
    if (this.cats.length >= MAX_CATS) return null
    const pos = position || this._randomSpawnPoint()
    const cat = new MouseFollowCharacterController({
      camera: this.camera,
      scene: this.scene,
      path: this.modelPath,
      world: this,
      genome,
      spawnPos: pos,
    })
    this.cats.push(cat)
    // All cats share the same array reference for neighbor queries
    for (const c of this.cats) c.collisionObjects = this.cats
    return cat
  }

  _randomSpawnPoint() {
    const r = 12 + Math.random() * 28
    const t = Math.random() * Math.PI * 2
    return new THREE.Vector3(Math.cos(t) * r, 0, Math.sin(t) * r)
  }

  update(dt) {
    this.ball.update(dt)
    for (const cat of this.cats) cat.update(dt)

    // Process retirements (fade out → remove)
    if (this._retiring.size) {
      for (const cat of this._retiring) {
        cat._retireT = (cat._retireT ?? 0) + dt
        if (cat.target) {
          const k = Math.max(0, 1 - cat._retireT / RETIRE_DURATION)
          cat.target.scale.setScalar(k)
        }
        if (cat._retireT >= RETIRE_DURATION) {
          this._removeCat(cat)
          this._retiring.delete(cat)
        }
      }
    }

    this._reproTimer -= dt
    if (this._reproTimer <= 0) {
      this._reproTimer = REPRO_INTERVAL
      this._tryReproduce()
    }

    this._evoTimer -= dt
    if (this._evoTimer <= 0) {
      this._evoTimer = EVO_INTERVAL
      this.evolve()
    }
  }

  // Force a generational step. Retires the least-fit cat(s) and spawns
  // descendants of the top two. Safe to call anytime (button-driven too).
  evolve() {
    const active = this.cats.filter((c) => !this._retiring.has(c))
    if (active.length < 2) return
    this.generation++

    const sorted = active.slice().sort((a, b) => (b.fitness ?? 0) - (a.fitness ?? 0))
    const [top1, top2] = sorted

    // Retire the worst 1-2 cats (scale-proportional to population)
    const retireCount = Math.max(1, Math.min(2, Math.floor(active.length / 3)))
    for (let i = 0; i < retireCount; i++) {
      const loser = sorted[sorted.length - 1 - i]
      if (loser && loser !== top1 && loser !== top2) this._retiring.add(loser)
    }

    // Spawn replacements (+1 for mild population pressure toward cap)
    const spawnCount = retireCount + (active.length < MAX_CATS ? 1 : 0)
    for (let i = 0; i < spawnCount; i++) {
      const child = crossGenome(top1.genome, top2.genome, 0.12)
      this.spawn(child)
    }
  }

  _removeCat(cat) {
    const idx = this.cats.indexOf(cat)
    if (idx !== -1) this.cats.splice(idx, 1)
    if (cat.target && cat.target.parent) cat.target.parent.remove(cat.target)
    // Keep collisionObjects refs consistent — everyone shares this.cats
    for (const c of this.cats) c.collisionObjects = this.cats
  }

  _tryReproduce() {
    if (this.cats.length >= MAX_CATS) return
    // Only mature cats (fully spawned) participate
    const mature = this.cats.filter((c) => c.isMature)
    for (let i = 0; i < mature.length; i++) {
      for (let j = i + 1; j < mature.length; j++) {
        const a = mature[i]
        const b = mature[j]
        if (!a.position || !b.position) continue
        if (a.genome.sociability < REPRO_MIN_SOCIABILITY) continue
        if (b.genome.sociability < REPRO_MIN_SOCIABILITY) continue
        const d = a.position.distanceTo(b.position)
        if (d > REPRO_MAX_DIST) continue
        const p = REPRO_BASE_P * (a.genome.sociability + b.genome.sociability) * 0.5
        if (Math.random() < p) {
          const childGenome = crossGenome(a.genome, b.genome, 0.09)
          const midPos = a.position.clone().lerp(b.position, 0.5)
          midPos.x += (Math.random() - 0.5) * 4
          midPos.z += (Math.random() - 0.5) * 4
          midPos.y = 0
          this.spawn(childGenome, midPos)
          return // one birth per tick max
        }
      }
    }
  }

  // Called by index.js click handler
  petCat(cat) { cat?.pet?.() }
  moveBall(point) { this.ball.placeAt(point) }

  // Save genomes ranked by rough "fitness" (happiness + age)
  save() {
    if (this._saved) return // avoid double-save (pagehide + beforeunload)
    this._saved = true
    try {
      const ranked = this.cats
        .slice()
        .sort((a, b) => (b.fitness ?? 0) - (a.fitness ?? 0))
        .map((c) => c.genome)
        .slice(0, 8)
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        generation: this.generation,
        savedAt: Date.now(),
        genomes: ranked,
      }))
    } catch (e) {
      // quota or disabled storage — ignore
    }
  }

  _loadSaved() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      if (!raw) return null
      return JSON.parse(raw)
    } catch { return null }
  }

  getStats() {
    return {
      count: this.cats.length,
      generation: this.generation,
    }
  }
}
